import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import type { NotificationCreateType } from 'webapp/enums/notifications';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Board, BoardLoop, MetricSetup } from 'webapp/models-custom';
import Report from 'webapp/models-custom/report';
import { themeClassNames } from 'webapp/models-custom/theme';

interface Args {
	model: MetricSetup | Board | BoardLoop | Report;
	origin: string;
	popupTheme?: string; // e.g. dark (when from dark board preview popup)
	buttonTheme?: string; // e.g "empty" (designer)
}

type Action = 'createNotification';

export default class NotificationsButtonComponent extends Component<Args> {
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('popup') declare popupService: Service.Popup;

	@tracked isPopupOpen = false;

	@cached
	get rawOptions(): PopupListItem<Action> | null {
		return this.mmNotificationsService.getNotificationsDropdownOptionForFeature(
			this.args.model,
			'createNotification',
		);
	}

	get options() {
		const options = this.rawOptions;
		if (options?.sublistItems) {
			return options.sublistItems;
		} else if (options) {
			return [options];
		} else {
			return [];
		}
	}

	get notificationCount() {
		return this.rawOptions?.used || 0;
	}

	get showNotifyText() {
		return !this.notificationCount;
	}

	get buttonThemeClass() {
		return themeClassNames(this.args.buttonTheme);
	}

	get tooltipText() {
		const optionsWithCount: PopupListItem<Action>[] = [];
		this.options.forEach((o) => {
			if (o.sublistItems?.length) {
				o.sublistItems.forEach((i) => {
					if (i.used) {
						optionsWithCount.push(i);
					}
				});
			} else {
				if (o.used) {
					optionsWithCount.push(o);
				}
			}
		});

		const optionsWithCountFormatted = optionsWithCount
			.map((o) => {
				// handle cases where there was just one item in sublist so we only return "Notifications" as for the dropdown
				// however, tooltip needs the actual type name here, so we use the helper on the id
				const text =
					o.text === 'Notifications'
						? this.mmNotificationsService.getNameForCreateType(
								o.id as NotificationCreateType,
							)
						: o.text;
				return `<li>${text} (${o.used})</li>`;
			})
			.join('');

		return `<p>Notifications</p><ul>${optionsWithCountFormatted}</ul>`;
	}

	@action
	openOptions(e: PointerEvent) {
		const itemsList = this.options;
		if (itemsList.length === 1) {
			this.createNotification(itemsList[0]!.id as NotificationCreateType);
			return;
		}

		const attachTo = (e.target as HTMLElement)?.closest<HTMLElement>('.notify-btn');

		if (!attachTo || !itemsList.length) {
			return;
		}

		const popup = this.popupService.createListPopup(
			attachTo,
			this.args.popupTheme,
			'board-options',
			true,
			true,
		);

		if (popup) {
			this.isPopupOpen = true;

			popup.open({
				title: 'Create notification',
				itemsList,
				hideDropdownSearch: false,
				doAction: (_actionName: unknown, ...args: unknown[]) => {
					this.createNotification(args[0] as NotificationCreateType);
				},
			});

			popup.on(PopupEvent.CLOSING, this, () => {
				this.isPopupOpen = false;
			});
		}
	}

	@action
	createNotification(type: NotificationCreateType) {
		// show the report/reminder tabs, same as the generic create flow
		const showCreateTabs = this.args.model instanceof Report;

		this.mmNotificationsService.createNewForFeature(
			type,
			this.args.model,
			this.args.origin,
			showCreateTabs,
		);
	}

	<template>
		<a
			class={{joinClasses
				(array "notify-btn" this.buttonThemeClass (unless this.showNotifyText "no-text"))
			}}
			{{on "click" this.openOptions}}
		>
			<CommonUiIconComponent @name="Bell" @size="md" />
			{{#if this.showNotifyText}}
				Notify
			{{else}}
				{{this.notificationCount}}
				{{#unless this.isPopupOpen}}
					<ToolTipComponent
						@text={{this.tooltipText}}
						@maxWidth={{250}}
						@extraClass="tooltip-with-title-and-list"
					/>
				{{/unless}}
			{{/if}}
		</a>
	</template>
}
